import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useDemo, InviteStatus } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { participants } from '../../data/demo';
import { assessmentUrlFor } from '../../data/demoInstruments';
const STATUS_STYLE: Record<InviteStatus, string> = {
  'NOT STARTED': 'border-rule text-muted',
  'IN PROGRESS': 'border-signal-amber/50 text-signal-amber',
  SUBMITTED: 'border-brand/40 text-brand',
  'AWAITING REVIEW': 'border-brand text-brand'
};
export function Invite() {
  const {
    mode,
    candidateCount,
    inviteStatuses,
    setInviteStatuses,
    bundle
  } = useDemo();
  const assessmentUrl = assessmentUrlFor(bundle.instrument);
  const [copied, setCopied] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();
  const rows = participants.slice(0, mode === 'single' ? 1 : Math.max(2, candidateCount));
  const simulate = () => {
    if (running) return;
    setRunning(true);
    const fill = (s: InviteStatus) => setInviteStatuses(rows.map(() => s));
    window.setTimeout(() => fill('IN PROGRESS'), 300);
    window.setTimeout(() => fill('SUBMITTED'), 1600);
    window.setTimeout(() => fill('AWAITING REVIEW'), 2700);
    window.setTimeout(() => navigate('/demo/record'), 3400);
  };
  const copy = (token: string) => {
    navigator.clipboard?.writeText(typeof window === 'undefined' ? assessmentUrl : `${window.location.origin}${assessmentUrl}`);
    setCopied(token);
    window.setTimeout(() => setCopied(null), 1500);
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-774" data-mp="2htek3udcuy7h">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-775" data-mp="2hs3erq49hie9">
        <LenticMark data-id="element-776" data-mp="1u2s9pwfvp1ub" />
        <BackLink to="/demo/instrument" label="Back to the instrument" data-id="element-777" data-mp="1u291x8jxztu0" />
      </div>

      <main className="mx-auto w-full max-w-[860px] flex-1 px-8 py-14" data-id="element-778" data-mp="1p3pcuza1y1xf">
        <p className="eyebrow text-brand" data-id="element-779" data-mp="3p7i70olrkznf">Invite</p>
        <h1 className="mt-3 text-[26px] font-semibold tracking-tight text-ink" data-id="element-780" data-mp="3p6qczi7tf9z9">
          {rows.length === 1 ? 'One tokenised link.' : `${rows.length} tokenised links.`}
        </h1>
        <p className="mt-2 max-w-lg text-[13.5px] leading-relaxed text-muted" data-id="element-781" data-mp="2rxhknfrwxz8">
          Identity stays in your enrolment list. The assessment record never carries a name.
        </p>

        <ul className="mt-8 divide-y divide-rule border-y border-rule" data-id="element-782" data-mp="3695wayf7fhpq">
          {rows.map((p, i) => {
          const status = inviteStatuses[i] || 'NOT STARTED';
          return <li key={p.id} className="grid grid-cols-[110px_minmax(0,1fr)_auto_auto] items-center gap-4 py-4" data-id="element-783" data-mp="3f8j4zovi829e" data-mpi={i}>
                <span className="font-mono-num text-[12px] text-ink" data-id="element-784" data-mp="3k8oqsof3eujk">{p.id}</span>
                <a href={assessmentUrl} target="_blank" rel="noreferrer" className="truncate font-mono-num text-[11.5px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink hover:decoration-brand" data-id="element-785" data-mp="ujd0a6e7m0k6">
                  {p.token}
                </a>
                <button type="button" onClick={() => copy(p.token)} className="inline-flex items-center gap-1.5 rounded-sm border border-rule bg-white px-2.5 py-1.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted transition-colors duration-150 ease-settle hover:border-ink hover:text-ink" data-id="element-786" data-mp="zv0a88khi3ab">
                  {copied === p.token ? <CheckIcon className="h-3 w-3" data-id="element-787" data-mp="gw5y77cengxu" /> : <CopyIcon className="h-3 w-3" data-id="element-788" data-mp="gx7ky92s9rve" />}
                  {copied === p.token ? 'copied' : 'copy'}
                </button>
                <span className={`w-[132px] rounded-sm border px-2.5 py-1 text-center font-mono-num text-[10.5px] uppercase tracking-wider transition-colors duration-150 ease-settle ${STATUS_STYLE[status]}`} data-id="element-789" data-mp="2baynkomp3bhc">
                  {status}
                </span>
              </li>;
        })}
        </ul>

        <p className="mt-4 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-790" data-mp="2jabudcnuubwd">
          Demo: all invite links open the same {bundle.label} build. Production issues one unique
          tokenised session per candidate.
        </p>

        <button type="button" onClick={simulate} disabled={running} className="mt-6 font-mono-num text-[11px] uppercase tracking-[0.12em] text-brand underline decoration-rule underline-offset-4 transition-colors hover:decoration-brand disabled:text-muted" data-id="element-791" data-mp="29fbhybela9nr">
          {running ? 'simulating…' : 'simulate completion'}
        </button>
      </main>

      <DemoFooter data-id="element-792" data-mp="1qkmniiogrhmu" />
    </div>;
}